import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Input,
} from '@caruso/ui';
import { venueReservationsService } from '@/services/venue-reservations.service';
import { venuesService } from '@/services/venues.service';
import { Plus, Calendar, Users, Building2, X, CalendarDays, ChevronDown } from 'lucide-react';
import { useRef, useEffect } from 'react';
import { ReservationStatus, PaymentStatus } from '@caruso/types';
import { cn } from '@caruso/ui';

type PeriodFilter = 'pending' | 'active' | 'today' | 'tomorrow' | 'future' | 'custom';

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'pending', label: 'Pendentes' },
  { value: 'active', label: 'Ativos' },
  { value: 'today', label: 'Hoje' },
  { value: 'tomorrow', label: 'Amanhã' },
  { value: 'future', label: 'Futuros' },
  { value: 'custom', label: 'Personalizado' },
];

export function VenueReservationsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('pending');
  const [selectedVenues, setSelectedVenues] = useState<string[]>([]);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [showVenueDropdown, setShowVenueDropdown] = useState(false);
  const venueDropdownRef = useRef<HTMLDivElement>(null);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (venueDropdownRef.current && !venueDropdownRef.current.contains(event.target as Node)) {
        setShowVenueDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Busca venues para o filtro
  const { data: venues } = useQuery({
    queryKey: ['venues'],
    queryFn: () => venuesService.getAll(false),
  });

  // Calcula as datas baseado no filtro de período
  const getDateFilters = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    switch (periodFilter) {
      case 'pending':
        return { status: 'pending' as ReservationStatus };
      case 'active':
        return { status: 'confirmed' as ReservationStatus };
      case 'today':
        return {
          startDate: formatDate(today),
          endDate: formatDate(today),
        };
      case 'tomorrow':
        return {
          startDate: formatDate(tomorrow),
          endDate: formatDate(tomorrow),
        };
      case 'future':
        return {
          startDate: formatDate(today),
        };
      case 'custom':
        return {
          startDate: customStartDate || undefined,
          endDate: customEndDate || undefined,
        };
      default:
        return {};
    }
  };

  const dateFilters = getDateFilters();

  const { data, isLoading } = useQuery({
    queryKey: ['venue-reservations', page, periodFilter, selectedVenues, customStartDate, customEndDate],
    queryFn: () => venueReservationsService.getAll({
      page: page.toString(),
      limit: '20',
      ...dateFilters,
      venueId: selectedVenues.length === 1 ? selectedVenues[0] : undefined,
    }),
  });

  // Filtrar por múltiplos venues no frontend se mais de um selecionado
  const filteredData = data?.data && selectedVenues.length > 1
    ? data.data.filter(r => selectedVenues.includes(r.venueId))
    : data?.data;

  const getStatusConfig = (status: ReservationStatus) => {
    const configs = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Pendente' },
      confirmed: { color: 'bg-green-500/20 text-green-400', label: 'Confirmada' },
      cancelled: { color: 'bg-red-500/20 text-red-400', label: 'Cancelada' },
      completed: { color: 'bg-blue-500/20 text-blue-400', label: 'Concluída' },
    };
    return configs[status] || configs.pending;
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  const handlePeriodChange = (period: PeriodFilter) => {
    setPeriodFilter(period);
    setPage(1);
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
    }
  };

  const handleVenueToggle = (venueId: string) => {
    setSelectedVenues(prev => {
      if (prev.includes(venueId)) {
        return prev.filter(id => id !== venueId);
      }
      return [...prev, venueId];
    });
    setPage(1);
  };

  const handleSelectAllVenues = () => {
    if (venues) {
      if (selectedVenues.length === venues.length) {
        setSelectedVenues([]);
      } else {
        setSelectedVenues(venues.map(v => v.id));
      }
    }
    setPage(1);
  };

  const clearVenueFilter = () => {
    setSelectedVenues([]);
    setPage(1);
  };

  const getVenueFilterLabel = () => {
    if (selectedVenues.length === 0) return 'Todos os ambientes';
    if (selectedVenues.length === 1) {
      const venue = venues?.find(v => v.id === selectedVenues[0]);
      return venue?.name || 'Ambiente selecionado';
    }
    return `${selectedVenues.length} ambientes`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-brand-gold">Reservas de Ambientes</h1>
          <p className="text-brand-ivory/60 mt-1">Gerencie as reservas dos espaços</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/app/venue-reservations/calendar')} variant="outline">
            <Calendar className="w-4 h-4 mr-2" />
            Calendário
          </Button>
          <Button onClick={() => navigate('/app/venue-reservations/new')} variant="premium">
            <Plus className="w-4 h-4 mr-2" />
            Nova Reserva
          </Button>
        </div>
      </div>

      {/* Filtros de Período */}
      <div className="flex flex-wrap gap-2">
        {PERIOD_OPTIONS.map((option) => (
          <Button
            key={option.value}
            variant={periodFilter === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePeriodChange(option.value)}
            className={cn(
              periodFilter === option.value
                ? 'bg-brand-gold text-brand-ink'
                : 'border-brand-gold/30 text-brand-ivory/70 hover:text-brand-gold hover:border-brand-gold'
            )}
          >
            {option.label}
          </Button>
        ))}

        {/* Filtro de Ambiente */}
        <div className="relative ml-auto" ref={venueDropdownRef}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVenueDropdown(!showVenueDropdown)}
            className={cn(
              'border-brand-gold/30',
              selectedVenues.length > 0
                ? 'bg-brand-gold/10 text-brand-gold border-brand-gold'
                : 'text-brand-ivory/70 hover:text-brand-gold hover:border-brand-gold'
            )}
          >
            <Building2 className="w-4 h-4 mr-2" />
            {getVenueFilterLabel()}
            <ChevronDown className={cn('w-4 h-4 ml-2 transition-transform', showVenueDropdown && 'rotate-180')} />
          </Button>

          {showVenueDropdown && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-brand-ink border border-brand-gold/20 rounded-lg shadow-xl z-50 p-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-brand-gold">Filtrar por Ambiente</span>
                  {selectedVenues.length > 0 && (
                    <button
                      onClick={clearVenueFilter}
                      className="text-xs text-brand-ivory/60 hover:text-brand-gold"
                    >
                      Limpar
                    </button>
                  )}
                </div>

                <label className="flex items-center space-x-2 pb-2 border-b border-brand-gold/10 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={venues ? selectedVenues.length === venues.length : false}
                    onChange={handleSelectAllVenues}
                    className="w-4 h-4 rounded border-brand-gold/30 bg-brand-smoke text-brand-gold focus:ring-brand-gold focus:ring-offset-0"
                  />
                  <span className="text-sm text-brand-ivory/80">
                    Selecionar todos
                  </span>
                </label>

                <div className="max-h-48 overflow-y-auto space-y-2">
                  {venues?.map((venue) => (
                    <label key={venue.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedVenues.includes(venue.id)}
                        onChange={() => handleVenueToggle(venue.id)}
                        className="w-4 h-4 rounded border-brand-gold/30 bg-brand-smoke text-brand-gold focus:ring-brand-gold focus:ring-offset-0"
                      />
                      <span className="text-sm text-brand-ivory/80 flex-1">
                        {venue.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Seletor de Data Personalizada */}
      {showCustomDatePicker && (
        <Card className="bg-brand-ink/50 border-brand-gold/20 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-brand-ivory/60">
              <CalendarDays className="w-4 h-4" />
              <span className="text-sm font-medium">Período:</span>
            </div>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStartDate}
                onChange={(e) => { setCustomStartDate(e.target.value); setPage(1); }}
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory w-40"
              />
              <span className="text-brand-ivory/60">até</span>
              <Input
                type="date"
                value={customEndDate}
                onChange={(e) => { setCustomEndDate(e.target.value); setPage(1); }}
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory w-40"
              />
            </div>

            {(customStartDate || customEndDate) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCustomStartDate('');
                  setCustomEndDate('');
                  setPage(1);
                }}
                className="text-brand-ivory/60 hover:text-brand-gold"
              >
                <X className="w-4 h-4 mr-1" />
                Limpar datas
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Tags de filtros ativos */}
      {selectedVenues.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedVenues.map((venueId) => {
            const venue = venues?.find(v => v.id === venueId);
            return (
              <span
                key={venueId}
                className="inline-flex items-center gap-1 px-2 py-1 bg-brand-gold/10 text-brand-gold text-sm rounded-lg"
              >
                {venue?.name}
                <button
                  onClick={() => handleVenueToggle(venueId)}
                  className="hover:text-brand-ivory"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-brand-ivory/50">Carregando reservas...</p>
        </div>
      ) : filteredData && filteredData.length > 0 ? (
        <div className="space-y-4">
          {filteredData.map((reservation) => (
            <Card
              key={reservation.id}
              className="bg-brand-ink/50 border-brand-gold/20 p-4 cursor-pointer hover:border-brand-gold/40"
              onClick={() => navigate(`/app/venue-reservations/${reservation.id}`)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-brand-gold">
                      {reservation.venue?.name}
                    </h3>
                    <span className={cn('px-2 py-1 rounded text-xs font-medium', getStatusConfig(reservation.status).color)}>
                      {getStatusConfig(reservation.status).label}
                    </span>
                  </div>
                  <p className="text-brand-ivory/80">{reservation.customerName}</p>
                  <div className="flex gap-4 mt-2 text-sm text-brand-ivory/60">
                    <span>{formatDate(reservation.startDate)} - {formatDate(reservation.endDate)}</span>
                    {reservation._count && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {reservation._count.guests} convidados
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-brand-gold">
                    {formatPrice(reservation.total)}
                  </div>
                  <div className="text-sm text-brand-ivory/60">
                    {reservation.paymentStatus === PaymentStatus.PAID ? '✓ Pago' : 'Pendente'}
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {data && data.meta.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Anterior
              </Button>
              <span className="px-4 py-2 text-brand-ivory/60">
                Página {page} de {data.meta.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page === data.meta.totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Card className="bg-brand-ink/50 border-brand-gold/20 p-12 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-brand-gold/50" />
          <p className="text-lg font-medium mb-2">Nenhuma reserva encontrada</p>
          <p className="text-brand-ivory/60 text-sm mb-4">
            {periodFilter === 'pending' && 'Não há reservas pendentes no momento'}
            {periodFilter === 'active' && 'Não há reservas ativas no momento'}
            {periodFilter === 'today' && 'Não há reservas para hoje'}
            {periodFilter === 'tomorrow' && 'Não há reservas para amanhã'}
            {periodFilter === 'future' && 'Não há reservas futuras'}
            {periodFilter === 'custom' && 'Não há reservas no período selecionado'}
          </p>
          <Button onClick={() => navigate('/app/venue-reservations/new')} variant="premium" className="mt-4">
            <Plus className="w-4 h-4 mr-2" />
            Criar Nova Reserva
          </Button>
        </Card>
      )}
    </div>
  );
}
